import { Suspense, lazy, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { filterTabs } from '../lib/permissions.js'

const TAB_BAR_VISIBLE_KEY = 'hagent_learning_tab_bar_visible'

const Learn = lazy(() => import('./Learn.jsx'))
const English = lazy(() => import('./English.jsx'))
const Mindmap = lazy(() => import('./Mindmap.jsx'))
const Notebook = lazy(() => import('./Notebook.jsx'))

function TabLoading() {
  return (
    <div className="flex h-full items-center justify-center text-xs font-semibold text-gray-500 dark:text-gray-500">
      Đang tải...
    </div>
  )
}

const tabs = [
  {
    id: 'learn',
    label: 'Code',
    icon: (
      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
        <path d="M4 4.5A2.5 2.5 0 016.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15z" />
        <path d="M8 7h8M8 11h6" />
      </svg>
    ),
  },
  {
    id: 'notebook',
    label: 'Notebook',
    icon: (
      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <path d="M8 7h8M8 11h8M8 15h5" />
        <path d="M3 7h1M3 11h1M3 15h1" />
      </svg>
    ),
  },
  {
    id: 'english',
    label: 'Tiếng Anh',
    icon: (
      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path d="M5 8h9" />
        <path d="M7 5v3c0 4 3 7 7 8" />
        <path d="M12 8c-.5 3-2.5 5.5-6 8" />
        <path d="M16 19l3-7 3 7" />
        <path d="M17 17h4" />
      </svg>
    ),
  },
  {
    id: 'accounting',
    label: 'IFRS',
    icon: (
      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2" />
        <path d="M9 5a2 2 0 012-2h2a2 2 0 012 2" />
        <path d="M9 14l2 2 4-4" />
      </svg>
    ),
  },
  {
    id: 'finance',
    label: 'CFA',
    icon: (
      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path d="M12 2v20M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
      </svg>
    ),
  },
  {
    id: 'data',
    label: 'Data',
    icon: (
      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path d="M4 7v10c0 2 1.5 4 4 4h8c2.5 0 4-2 4-4V7" />
        <path d="M4 7c0-2 1.5-4 4-4h8c2.5 0 4 2 4 4" />
        <path d="M12 12v.01M9 12v.01M15 12v.01" />
      </svg>
    ),
  },
  {
    id: 'mindmap',
    label: 'Mindmap',
    icon: (
      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="3" />
        <path d="M12 9V4M12 20v-5M9 12H4M20 12h-5M14.2 9.8l3.5-3.5M6.3 17.7l3.5-3.5M14.2 14.2l3.5 3.5M6.3 6.3l3.5 3.5" />
      </svg>
    ),
  },
]

export default function LearningHub({ token, provider, cxModel, user }) {
  const [activeTab, setActiveTab] = useState(() => {
    const saved = localStorage.getItem('hagent_learning_tab')
    return saved === 'review' ? 'learn' : (saved || 'learn')
  })
  const [showTabBar, setShowTabBar] = useState(() => localStorage.getItem(TAB_BAR_VISIBLE_KEY) !== 'false')

  const visibleTabs = filterTabs(user, 'learning', tabs)
  const effectiveTab = visibleTabs.some(t => t.id === activeTab) ? activeTab : visibleTabs[0]?.id

  function selectTab(tab) {
    setActiveTab(tab)
    localStorage.setItem('hagent_learning_tab', tab)
  }

  function toggleTabBar() {
    setShowTabBar(value => {
      const next = !value
      localStorage.setItem(TAB_BAR_VISIBLE_KEY, String(next))
      return next
    })
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-[var(--color-bg)]">
      <div className={`relative sticky top-0 z-30 flex shrink-0 items-center justify-between gap-2 bg-white/90 dark:bg-[#1e1e1e]/90 backdrop-blur-xl transition-all ${showTabBar ? 'border-b border-black/[0.12] dark:border-gray-700 px-2 py-1 sm:px-3' : 'h-0'}`}>
        {showTabBar && (
          <div className="flex min-w-0 items-center gap-1 overflow-x-auto no-scrollbar rounded-lg bg-gray-100 dark:bg-gray-800 p-0.5">
            {visibleTabs.map(tab => {
              const isActive = effectiveTab === tab.id
              return (
                <button
                  key={tab.id}
                  onClick={() => selectTab(tab.id)}
                  className={`relative flex h-7 shrink-0 items-center gap-1 rounded-md px-2 text-[11px] font-semibold transition-all ${
                    isActive
                      ? 'bg-gray-600 text-white shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:bg-white/70 dark:hover:bg-[#1e1e1e]/70 hover:text-gray-900 dark:hover:text-white'
                  }`}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                </button>
              )
            })}
          </div>
        )}
        <button
          onClick={toggleTabBar}
          title={showTabBar ? 'Ẩn thanh tab' : 'Hiện thanh tab'}
          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#1e1e1e] text-gray-500 dark:text-gray-400 shadow-sm transition-all hover:border-amber-300 hover:text-amber-600 active:scale-95 ${showTabBar ? '' : 'absolute right-2 top-2'}`}
        >
          {showTabBar ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          <span className="sr-only">{showTabBar ? 'Ẩn thanh tab' : 'Hiện thanh tab'}</span>
        </button>
      </div>

      <div className="min-h-0 flex-1 overflow-hidden">
        <Suspense fallback={<TabLoading />}>
          {effectiveTab === 'learn' && (
            <div className="h-full min-h-0 overflow-hidden">
              <Learn token={token} provider={provider} cxModel={cxModel} />
            </div>
          )}

          {effectiveTab === 'english' && (
            <div className="h-full min-h-0 overflow-hidden">
              <English token={token} provider={provider} cxModel={cxModel} />
            </div>
          )}

          {effectiveTab === 'accounting' && (
            <div className="h-full min-h-0 overflow-hidden">
              <Learn token={token} provider={provider} cxModel={cxModel} defaultTrack="accounting" defaultTrackLabel="Kế toán IFRS" />
            </div>
          )}

          {effectiveTab === 'finance' && (
            <div className="h-full min-h-0 overflow-hidden">
              <Learn token={token} provider={provider} cxModel={cxModel} defaultTrack="finance" defaultTrackLabel="CFA - Tài chính" />
            </div>
          )}

          {effectiveTab === 'data' && (
            <div className="h-full min-h-0 overflow-hidden">
              <Learn token={token} provider={provider} cxModel={cxModel} defaultTrack="data" defaultTrackLabel="Data Analytics" />
            </div>
          )}

          {effectiveTab === 'notebook' && (
            <div className="h-full min-h-0 overflow-hidden">
              <Notebook token={token} />
            </div>
          )}

          {effectiveTab === 'mindmap' && (
            <div className="h-full min-h-0 overflow-hidden">
              <Mindmap user={user} />
            </div>
          )}
        </Suspense>
      </div>
    </div>
  )
}
